import { assertEvent, type DoneActorEvent } from 'xstate';

import { OrderState } from '@sg/graphql-schema';
import { type GuardArgs } from '@sg/machines';

import {
  type FetchWantedTimesResponse,
  type LeaveGroupOrderResponse,
} from './ordering-machine.actors';
import { type OrderingContext } from './ordering-machine.context';
import { type OrderingEvent } from './ordering-machine.events';
import { orderingMachineLog } from './ordering-machine.logger';
import { checkIfCartCanShowUpsells, isWantedTimeToday } from './ordering-machine.utils';

// ─────────────────────────────────────────────────────────────────────────────

const checkIfLoggedIn: Guard = () => {
  orderingMachineLog.warn('Please provide `checkIfLoggedIn` guard.');
  return false;
};

const checkIfShouldFetchInitialPaymentState: Guard = () => {
  orderingMachineLog.warn('Please provide `checkIfShouldFetchInitialPaymentState` guard.');
  return false;
};

const checkIfBagCartPollingEnabled: Guard = () => {
  orderingMachineLog.warn('Please provide `checkIfBagCartPollingEnabled` guard.');
  return false;
};

const checkIfHasWantedTimes: Guard = ({ event }) => {
  const doneEvent = event as DoneActorEvent<FetchWantedTimesResponse>;
  const wantedTimes = doneEvent.output?.wantedTimes;

  return Array.isArray(wantedTimes) && wantedTimes.length > 0;
};

const checkIfHasNoPaymentMethods: Guard = ({ event }) => {
  assertEvent(event, 'xstate.done.actor.fetchPaymentMethods');
  const paymentMethods = event.output.paymentMethods;

  return !Array.isArray(paymentMethods) || paymentMethods.length === 0;
};

const checkIfDoesNotNeedsToShowFutureDateModal: Guard = ({ context, event }) => {
  assertEvent(event, 'CHANGE_TIME');
  return !event.params.isFutureDate || context.hasShownFutureDateWarning;
};

const checkIfNeedsToShowFutureDateModal: Guard = ({ context, event }) => {
  assertEvent(event, 'CHANGE_TIME');
  return event.params.isFutureDate && !context.hasShownFutureDateWarning;
};

const checkIfOrderSubmissionSuccess: Guard = ({ event }) => {
  assertEvent(event, 'xstate.done.actor.submitOrder');
  return Boolean(event.output?.success);
};

const checkIfTimeslotIsUnavailable: Guard = ({ event }) => {
  assertEvent(event, 'xstate.done.actor.submitOrder');
  return Boolean(!event.output?.success && event.output?.isTimeslotUnavailable);
};

const checkIfWillBeFutureOrder: Guard = ({ context, event }) => {
  assertEvent(event, 'xstate.done.actor.submitOrder');

  if (event.output?.success || !event.output?.isTimeslotUnavailable) {
    return false;
  }

  const nextTimeslot = context.cart.wantedTimes.find(
    (wantedTime) => wantedTime !== context.wantedTime,
  );

  return !isWantedTimeToday(nextTimeslot);
};

const checkIfNextTimeslotIsTomorrow: Guard = ({ context }) => {
  const nextTimeslot = context.cart.wantedTimes.find(
    (wantedTime) => wantedTime !== context.wantedTime,
  );

  return !isWantedTimeToday(nextTimeslot);
};

const checkIfGenericOrderSubmissionError: Guard = ({ event }) => {
  assertEvent(event, 'xstate.done.actor.submitOrder');
  return Boolean(!event.output?.success && !event.output?.isTimeslotUnavailable);
};

const checkIfGiftCardApplied: Guard = ({ event }) => {
  assertEvent(event, 'xstate.done.actor.applyGiftCard');
  const balance = (event.output as { giftCardBalance?: number } | null)?.giftCardBalance;
  return Boolean(balance);
};

const checkIfGiftCardNotApplied: Guard = ({ event }) => {
  assertEvent(event, 'xstate.done.actor.applyGiftCard');
  const balance = (event.output as { giftCardBalance?: number } | null)?.giftCardBalance;
  return !balance;
};

const checkIfRewardApplied: Guard = ({ event }) => {
  assertEvent(event, 'xstate.done.actor.applyReward');
  return Boolean('cart' in event.output);
};

const checkIfRewardNotApplied: Guard = ({ event }) => {
  assertEvent(event, 'xstate.done.actor.applyReward');
  return Boolean('error' in event.output);
};

const checkIfRewardRemoved: Guard = ({ event }) => {
  assertEvent(event, 'xstate.done.actor.removeReward');
  return Boolean('cart' in event.output);
};

const checkIfRewardNotRemoved: Guard = ({ event }) => {
  assertEvent(event, 'xstate.done.actor.removeReward');
  return Boolean('error' in event.output);
};

const checkIfPromoCodeApplied: Guard = ({ event }) => {
  assertEvent(event, 'xstate.done.actor.applyPromoCode');
  return Boolean(event.output?.success);
};

const checkIfGroupOrderLeft: Guard<LeaveGroupOrderResponse> = (_args, params) => {
  return params.success;
};

const checkIfGroupOrderNeedsLock: Guard = ({ context }) => {
  const canManageGroupOrder = Boolean(context.cart.isGroupOrder && context.cart.canSubmitOrder);
  const isCartLockedForCheckout = context.cart.orderState === OrderState.LockedCheckout;

  return canManageGroupOrder && !isCartLockedForCheckout;
};

const checkIfGroupOrderNeedsUnlock: Guard = ({ context }) => {
  const canManageGroupOrder = Boolean(context.cart.isGroupOrder && context.cart.canSubmitOrder);
  const isCartLockedForCheckout = context.cart.orderState === OrderState.LockedCheckout;

  return canManageGroupOrder && isCartLockedForCheckout;
};

const checkIfCanShowUpsells: Guard = ({ context }) => {
  return checkIfCartCanShowUpsells(context.cart);
};

// ─────────────────────────────────────────────────────────────────────────────

export const OrderingGuards = {
  checkIfLoggedIn,
  checkIfBagCartPollingEnabled,
  checkIfHasWantedTimes,
  checkIfHasNoPaymentMethods,
  checkIfDoesNotNeedsToShowFutureDateModal,
  checkIfNeedsToShowFutureDateModal,
  checkIfOrderSubmissionSuccess,
  checkIfTimeslotIsUnavailable,
  checkIfWillBeFutureOrder,
  checkIfNextTimeslotIsTomorrow,
  checkIfGenericOrderSubmissionError,
  checkIfGiftCardApplied,
  checkIfGiftCardNotApplied,
  checkIfRewardApplied,
  checkIfRewardNotApplied,
  checkIfRewardRemoved,
  checkIfRewardNotRemoved,
  checkIfPromoCodeApplied,
  checkIfGroupOrderLeft,
  checkIfGroupOrderNeedsLock,
  checkIfGroupOrderNeedsUnlock,
  checkIfCanShowUpsells,
  checkIfShouldFetchInitialPaymentState,
};

// ─── Types ───────────────────────────────────────────────────────────────────

type Guard<Params extends Record<string, unknown> | void = void> = (
  args: GuardArgs<OrderingContext, OrderingEvent>,
  params: Params,
) => boolean;
